import React from 'react';
import styled, { css } from 'styled-components';
import searchIcon from '../../../asset/icon/see.png';
import searchNotIcon from '../../../asset/icon/see_not.png';
import HintSmallWrapper from '../../../shared/component/headless/button/HintSmallWrapper';
import { getHintMessage } from '../../../shared/constants/hintMessages';
import { WorkContentMode } from '../../../shared/enum/EnumRepository';
import { useWorkContentModeStore } from '../../../shared/store/workContentModeStore';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useWorkPropertyVisible } from '../../../shared/hooks/useApi/useWorkPropertyVisible';
import { UpdateWorkPropertyVisibleReq } from '../../../shared/dto/ReqDtoRepository';
import { useWorkViewStore } from '../../../shared/store/WorkViewStore';

const WorkTopBar: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { workPropertyVisible, updateEngVisible } = useWorkPropertyVisible();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const setWorkContentMode = useWorkContentModeStore((s) => s.setWorkContentMode);

  if (!workPropertyVisible) return null;

  const engVisible = workPropertyVisible.engVisible !== false;

  if (!isEditMode && !engVisible) return null;

  const isEngMode = workContentMode === WorkContentMode.ENG;
  const showLanguageOptions = isEditMode && engVisible;

  const handleEngVisible = async (value: boolean) => {
    await updateEngVisible({
      ...workPropertyVisible,
      engVisible: value,
    } as UpdateWorkPropertyVisibleReq);
  };

  const handleToggleLanguage = () => {
    setWorkContentMode(isEngMode ? WorkContentMode.Orig : WorkContentMode.ENG);
  };

  return (
    <TopBar $isEditMode={isEditMode} $hasActiveWork={activeWork !== null}>
      <LanguageSelector>
        {showLanguageOptions && (
          <>
            <LanguageOption
              type="button"
              $isActive={workContentMode === WorkContentMode.Orig}
              onClick={() => setWorkContentMode(WorkContentMode.Orig)}
            >
              Orig
            </LanguageOption>
            <LanguageDivider>/</LanguageDivider>
            <LanguageOption
              type="button"
              $isActive={workContentMode === WorkContentMode.ENG}
              onClick={() => setWorkContentMode(WorkContentMode.ENG)}
            >
              ENG
            </LanguageOption>
          </>
        )}
        {!isEditMode && (
          <LanguageOption
            type="button"
            $isActive={isEngMode}
            onClick={handleToggleLanguage}
          >
            {isEngMode ? 'ENG' : 'Orig'}
          </LanguageOption>
        )}
        {isEditMode && (
          <>
            {engVisible && <LanguageDivider>|</LanguageDivider>}
            <EngVisibilityIconWrap>
              <HintSmallWrapper
                content={
                  engVisible
                    ? getHintMessage('WORK_ENG_VISIBILITY_HIDE')
                    : getHintMessage('WORK_ENG_VISIBILITY_EXPAND')
                }
                toLeft={true}
              >
                <EngVisIconBtnWrapper onClick={() => void handleEngVisible(!engVisible)}>
                  <EngVisIconBtn $backgroundImg={engVisible ? searchIcon : searchNotIcon} />
                </EngVisIconBtnWrapper>
              </HintSmallWrapper>
            </EngVisibilityIconWrap>
          </>
        )}
      </LanguageSelector>
    </TopBar>
  );
};

export default WorkTopBar;

const TopBar = styled.div<{ $isEditMode: boolean; $hasActiveWork: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  ${({ $isEditMode, $hasActiveWork, theme }) => !$isEditMode && !$hasActiveWork && css`
    padding-left: 8px;
    margin-left: 4px;
    border-left: 0.5px solid ${theme.colors.color_Gray_04};
  `}
`;

const LanguageSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EngVisibilityIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
`;

const EngVisIconBtnWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.color_Alpha_03};
  &:hover {
    transform: scale(1.1);
  }
  transition: all 0.3s ease-in-out;
  cursor: pointer;
`;

const EngVisIconBtn = styled.div<{ $backgroundImg: string }>`
  width: clamp(12px, 2vw, 14px);
  height: clamp(12px, 2vw, 14px);
  background-image: url(${({ $backgroundImg }) => $backgroundImg});
  background-size: cover;
  background-position: center;
`;

const LanguageOption = styled.button<{ $isActive: boolean }>`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.color_Gray_04 : theme.colors.color_Gray_05};
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.color_Gray_04};
  }
`;

const LanguageDivider = styled.span`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_05};
  user-select: none;
`;
